import { type FC, useMemo } from "react";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useJobColors } from "../../../../hooks/useJobColors";
import { useRoleGroupColors } from "../../../../hooks/useRoleColors";
import { INK, LETTER_SPACING } from "../../../../theme/tokens";
import MetaChip from "../../../UIToolkit/Badges/MetaChip";
import { getSectionTone } from "../../WeeklyGapView/consts";
import type { GapsShiftGroup } from "../gapsDayModel";
import { ROW_BADGE_GEOMETRY_SX } from "../gapsRowLayout";

type Props = {
	group: Pick<GapsShiftGroup, "jobLabel" | "isJobSection" | "jobId">;
	/**
	 * At a list row's density — the height and type of the rows' role badge (`ROW_BADGE_GEOMETRY_SX`) — for a chip that sits
	 * in a line of people (Open Shifts' applicants) rather than in a heading.
	 */
	dense?: boolean;
	dataTestId?: string;
};

/** A row's badge scale, read off the role badge itself, so a job chip and a role chip in one list line up exactly. */
const denseSx = {
	minHeight: ROW_BADGE_GEOMETRY_SX.height,
	px: ROW_BADGE_GEOMETRY_SX.px,
	py: 0,
	borderRadius: ROW_BADGE_GEOMETRY_SX.borderRadius,
	fontSize: ROW_BADGE_GEOMETRY_SX.fontSize,
} as const;

/**
 * A group's job as the home's group header draws it — the same `MetaChip` in the section's own colour
 * (`getSectionTone(...).badge`, the desktop weekly view's) — so a sheet about "Nurse 7a-3p" leads with the badge the
 * user just tapped next to, and a list of groups reads like the home it came from.
 */
export const GapsJobBadge: FC<Props> = ({ group, dense, dataTestId }) => {
	const { chosenDepartment } = useHospitalData();
	const jobColor = useJobColors();
	const roleGroupColor = useRoleGroupColors();
	const tone = useMemo(
		() =>
			getSectionTone(
				{ shortRole: group.jobLabel, isJobSection: group.isJobSection, jobId: group.jobId },
				chosenDepartment.role_groups,
				jobColor,
				roleGroupColor,
			),
		[group.jobLabel, group.isJobSection, group.jobId, chosenDepartment.role_groups, jobColor, roleGroupColor],
	);
	return (
		<MetaChip
			label={group.jobLabel}
			size="small"
			customTone={{ bg: tone.badge, ink: INK.PRIMARY }}
			dataTestId={dataTestId}
			sx={{ textTransform: "uppercase", letterSpacing: LETTER_SPACING.LABEL, fontWeight: 600, flex: "none", ...(dense ? denseSx : {}) }}
		/>
	);
};
